import { queryClient } from '@/graphql/client';
import { FIND_PHOTO_BY_FILE_ID } from '@/graphql/queries';
import { request } from '@/graphql/request';
import { imageLoadingOverlayStore } from '@/stores/imageLoadingOverlay';
import {
  Panorama,
  Photo2D,
  resolvePhotoGroupCore,
  startPanoramaWalkthrough,
  startPhotoOverlay,
  updatePhotoUrlFromRenderObjectImage,
  useViewer,
} from '@/stores/viewer';
import { persist } from '@/utils/Persist';
import { createCameraModel, createPanorama, createPhoto2D } from '@/utils/transformers';
import { Panorama as PanoramaModel } from '@skand/viewer-component-v2';
import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';

const initialImageOverrideAtom = atom(true);

export const useStartPhotoOrPanorama = () => {
  const api3D = useViewer(state => state.api3D);
  const photo2DGroups = useViewer(state => state.photo2DGroups);
  const panoramaGroups = useViewer(state => state.panoramaGroups);
  const [initialImageOverride, setInitialImageOverride] = useAtom(initialImageOverrideAtom);

  // Enable 2D Viewer and Panorama with URL image field
  useEffect(() => {
    const showDefaultPhoto = async () => {
      const persistImage = persist.get('image');

      if (!initialImageOverride) return;
      if (!persistImage) return;
      if (!api3D) return;
      if (photo2DGroups.length === 0 && panoramaGroups.length === 0) return;

      setInitialImageOverride(false); // prevent re-rendering

      // fetch photo by id
      const renderObjectIds = [...photo2DGroups, ...panoramaGroups].map(
        group => group.renderObjectId,
      );
      const query = await queryClient.fetchQuery({
        queryFn: () => request(FIND_PHOTO_BY_FILE_ID, { fileId: persistImage, renderObjectIds }),
        queryKey: ['FIND_PHOTO_BY_FILE_ID', persistImage],
      });
      imageLoadingOverlayStore.next(); // step 1 complete

      const imageProjection = query.findImageProjectionRenderObjectAndImageByFileId;
      const panoramic = query.findPanoramicRenderObjectAndImageByFileId;

      if (imageProjection?.renderObject && imageProjection?.renderObjectImage) {
        const { renderObject, renderObjectImage } = imageProjection;
        const oldGroup = photo2DGroups.find(group => group.renderObjectId === renderObject.id);
        if (!oldGroup) return;

        const oldGroupPhotos = oldGroup.photos;
        const oldPhoto = oldGroupPhotos.find(photo => photo.id === persistImage) as Photo2D;
        const newPhoto = (await createPhoto2D(renderObjectImage, oldGroup, api3D)) as Photo2D;
        for (const cameraModel of renderObject.cameraModels ?? []) {
          if (!cameraModel) continue;
          const newCamera = createCameraModel(cameraModel);
          if (newCamera) oldGroup.cameraModels.set(newCamera.id, newCamera);
        }

        const newGroupPhotos = oldPhoto
          ? oldGroupPhotos.map(photo => (photo.id === newPhoto?.id ? newPhoto : photo))
          : [...oldGroupPhotos, newPhoto];
        const newGroup = { ...oldGroup, photos: newGroupPhotos };
        resolvePhotoGroupCore(newGroup);
        await updatePhotoUrlFromRenderObjectImage(newPhoto, renderObjectImage);
        imageLoadingOverlayStore.next(); // step 2 complete

        await startPhotoOverlay(newPhoto);
      }

      if (panoramic?.renderObject && panoramic?.renderObjectImage) {
        const { renderObject, renderObjectImage } = panoramic;
        const oldGroup = panoramaGroups.find(group => group.renderObjectId === renderObject.id);
        if (!oldGroup) return;

        const oldGroupPhotos = oldGroup.photos;
        const oldPhoto = oldGroupPhotos.find(photo => photo.id === persistImage) as Panorama;
        const newPhoto = (await createPanorama(renderObjectImage, oldGroup, api3D)) as Panorama;
        const newGroupPhotos = oldPhoto
          ? oldGroupPhotos.map(photo => (photo.id === newPhoto?.id ? newPhoto : photo))
          : [...oldGroupPhotos, newPhoto];
        const newGroup = { ...oldGroup, photos: newGroupPhotos };
        resolvePhotoGroupCore(newGroup);
        await updatePhotoUrlFromRenderObjectImage(newPhoto, renderObjectImage);
        imageLoadingOverlayStore.next(); // step 2 complete

        if (newPhoto.widget) {
          api3D?.panorama
            .getWalkthrough()
            .setPanoramas([newPhoto.widget.getModel() as PanoramaModel]);
        }
        await startPanoramaWalkthrough(newPhoto);
      }

      imageLoadingOverlayStore.next(); // step 3 complete
      imageLoadingOverlayStore.hide(); // make sure the overlay is hidden
    };

    showDefaultPhoto();
  }, [api3D, initialImageOverride, panoramaGroups, photo2DGroups, setInitialImageOverride]);
};
